import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Field as RingilField } from '@components/framework/formfield/Field.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import InputSelectMultiple from '@components/ringil3/Input/InputSelect/InputSelectMultiple';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch';
import { CalendarViewType } from '../calendarView';

interface Props {
  visibleRampsCount?: number;
  onVisibleRampsCountChange: (count?: number) => void;
  currentView: CalendarViewType;
  onViewChange: (view: 'day' | 'week' | 'workWeek') => void;
  weekDisabled: boolean;
  showLargeView: boolean;
  setShowLargeView: (value: boolean) => void;
  statisticsEnabled: boolean;
  statsOpened: boolean;
  setStatsOpened: (value: boolean) => void;
  statsActive: boolean[];
  setStatsActive: (value: boolean[]) => void;
}

/** Body of the "Adjust calendar style" sidebar. */
export const CalendarSettings: React.FC<Props> = ({
  visibleRampsCount,
  onVisibleRampsCountChange,
  currentView,
  onViewChange,
  weekDisabled,
  showLargeView,
  setShowLargeView,
  statisticsEnabled,
  statsOpened,
  setStatsOpened,
  statsActive,
  setStatsActive,
}) => {
  const { tr } = useTranslationLgs();

  return (
    <div className="flex flex-col gap-4">
      <RingilField
        label={tr('TimeslotReservations.RampCount', 'Ramp count')}
        helpText={tr('TimeslotReservations.RampCountHelpText', 'Select how many ramps should be visible on the calendar at once.')}
      >
        <InputSelect
          model={[
            { code: '', label: tr('TimeslotReservations.AllRamps', 'All ramps') },
            { code: '1', label: tr('TimeslotReservations.OneRamp', 'One ramp') },
            { code: '3', label: tr('TimeslotReservations.ThreeRamps', 'Three ramps') },
            { code: '5', label: tr('TimeslotReservations.FiveRamps', 'Five ramps') },
          ]}
          selectedItemCode={visibleRampsCount !== undefined ? String(visibleRampsCount) : ''}
          onSelect={(v: string) => onVisibleRampsCountChange(v === '' ? undefined : parseInt(v, 10))}
        />
      </RingilField>
      <RingilField
        label={tr('TimeslotReservations.ShownInterval', 'Shown interval')}
        helpText={tr('TimeslotReservations.ShownIntervalHelpText', 'Select the time interval displayed on the calendar.')}
        disabled={weekDisabled}
      >
        <InputSelect
          model={[
            { code: 'day', label: tr('TimeslotReservations.Day', '1 Den') },
            { code: 'week', label: tr('TimeslotReservations.AllWeek', 'Celý týden') },
            { code: 'workWeek', label: tr('TimeslotReservations.WorkWeek', 'Pondělí - Pátek') },
          ]}
          disabled={weekDisabled}
          selectedItemCode={currentView === 'timeGridWeek' ? 'week' : currentView === 'timeGridWorkWeek' ? 'workWeek' : 'day'}
          onSelect={(v: string) => {
            if ((v === 'week' || v === 'workWeek') && weekDisabled) return;
            onViewChange(v as 'day' | 'week' | 'workWeek');
          }}
        />
      </RingilField>
      <RingilField
        label={tr('TimeslotReservations.PageInterval', 'Page Interval')}
        helpText={tr('TimeslotReservations.PageIntervalHelpText', 'Prefered page interval for the calendar view.')}
      >
        <InputSelect
          model={[
            { code: 'zoomIn', label: tr('TimeslotReservations.ZoomIn', 'Zoom In') },
            { code: 'zoomOut', label: tr('TimeslotReservations.ZoomOut', 'Zoom Out') },
          ]}
          selectedItemCode={showLargeView ? 'zoomIn' : 'zoomOut'}
          onSelect={(v: string) => setShowLargeView(v === 'zoomIn')}
        />
      </RingilField>
      {statisticsEnabled && (
        <div className="flex flex-col gap-2">
          <RingilInputSwitch
            name="toggle"
            label={tr(`TimeslotReservations.ShowAggregatedData`, `Show Aggregated Data`)}
            value={statsOpened}
            onChange={() => {
              setStatsOpened(!statsOpened);
              if (!statsOpened) {
                setStatsActive([true, true, true]);
              }
            }}
          />

          <InputSelectMultiple
            model={[
              { code: '0', label: tr('TimeslotReservations.ReservationsCount', 'Reservations Count') },
              { code: '1', label: tr('TimeslotReservations.ManipulationUnitsCount', 'Manipulation Units Count') },
              { code: '2', label: tr('TimeslotReservations.OrdersCount', 'Orders Count') },
            ]}
            selectedItemCodes={statsActive.map((active, index) => (active ? String(index) : null)).filter(Boolean)}
            onSelect={(v: string[]) => {
              const selectedIndices = v ? v : [];
              const newStatsActive = [selectedIndices.includes('0'), selectedIndices.includes('1'), selectedIndices.includes('2')];
              setStatsActive(newStatsActive);

              if (!newStatsActive.includes(true)) {
                setStatsOpened(false);
              }
            }}
            disabled={!statsOpened}
          />
        </div>
      )}
    </div>
  );
};